import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBrain, FiHelpCircle, FiAlertTriangle } = FiIcons;

const QuestionCard = ({ question, answer, onAnswer, onAIHelp }) => {
  const [localAnswer, setLocalAnswer] = useState(answer || '');

  const handleInputChange = (value) => {
    setLocalAnswer(value);
    onAnswer(question.id, value);
  };

  const renderInput = () => {
    switch (question.type) {
      case 'text':
        return (
          <input
            type="text"
            value={localAnswer}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={localAnswer}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            rows={4}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
          />
        );
      
      case 'select':
        return (
          <select
            value={localAnswer}
            onChange={(e) => handleInputChange(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          >
            <option value="">Select an option...</option>
            {question.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      
      case 'radio':
        return (
          <div className="space-y-3">
            {question.options.map((option) => (
              <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="radio"
                  name={question.id}
                  value={option.value}
                  checked={localAnswer === option.value}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                />
                <span className="text-slate-700">{option.label}</span>
                {option.description && (
                  <span className="text-sm text-slate-500">({option.description})</span>
                )}
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="space-y-3">
            {question.options.map((option) => (
              <label key={option.value} className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={Array.isArray(localAnswer) && localAnswer.includes(option.value)}
                  onChange={(e) => {
                    const currentValues = Array.isArray(localAnswer) ? localAnswer : [];
                    const newValues = e.target.checked
                      ? [...currentValues, option.value]
                      : currentValues.filter(v => v !== option.value);
                    handleInputChange(newValues);
                  }}
                  className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                />
                <span className="text-slate-700">{option.label}</span>
              </label>
            ))}
          </div>
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={localAnswer}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={question.placeholder}
            min={question.min}
            max={question.max}
            step={question.step}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-effect rounded-2xl p-8"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-slate-800 mb-3">{question.title}</h2>
          {question.description && (
            <p className="text-slate-600 leading-relaxed">{question.description}</p>
          )}
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          {question.helpText && (
            <button
              onClick={() => alert(question.helpText)}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
              title="Help"
            >
              <SafeIcon icon={FiHelpCircle} />
            </button>
          )}
          
          <button
            onClick={onAIHelp}
            className="p-2 text-blue-600 hover:text-blue-700 transition-colors"
            title="AI Assistant"
          >
            <SafeIcon icon={FiBrain} />
          </button>
        </div>
      </div>

      {/* Input */}
      <div className="mb-6">
        {renderInput()}
      </div>

      {/* AI Suggestions */}
      {question.aiSuggestion && (
        <div className="ai-suggestion rounded-lg p-4 mb-4">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiBrain} className="text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-slate-800 mb-1">AI Suggestion</h4>
              <p className="text-sm text-slate-700">{question.aiSuggestion}</p>
            </div>
          </div>
        </div>
      )}

      {/* Warnings */}
      {question.warning && localAnswer && (
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiAlertTriangle} className="text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800 mb-1">Important Notice</h4>
              <p className="text-sm text-amber-700">{question.warning}</p>
            </div>
          </div>
        </div>
      )}

      {/* Required indicator */}
      {question.required && (
        <p className="text-sm text-slate-500 mt-2">* This field is required</p>
      )}
    </motion.div>
  );
};

export default QuestionCard;